"use client";

import * as React from "react";
import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { createOrganisation } from "@/services/organisation";
import type { CreateOrganisationDto } from "@/types";

interface AddOrganisationDialogProps {
  children?: React.ReactNode;
  onSuccess?: () => void;
}

export function AddOrganisationDialog({
  children,
  onSuccess,
}: AddOrganisationDialogProps) {
  const [open, setOpen] = React.useState(false);
  const [name, setName] = React.useState("");

  const queryClient = useQueryClient();

  // Mutation for creating an organisation
  const mutation = useMutation({
    mutationFn: (data: CreateOrganisationDto) => createOrganisation(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["organisations"] });
      toast.success(`Organisation "${data.name}" created successfully!`);

      // Reset form
      setName("");
      setOpen(false);
      onSuccess?.();
    },
    onError: (error: any) => {
      const errorMessage =
        typeof error === "string"
          ? error
          : error?.message || "Failed to create organisation";
      toast.error(errorMessage);
      console.error("Error creating organisation:", error);
    },
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error("Organisation name is required");
      return;
    }

    const payload: CreateOrganisationDto = {
      name: name.trim(),
    };

    mutation.mutate(payload);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children || <Button>Add Organisation</Button>}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Add New Organisation</DialogTitle>
          <DialogDescription>
            Create a new organisation. Fill in the details below.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            {/* Organisation Name */}
            <div className="grid gap-2">
              <Label htmlFor="name">
                Organisation Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                placeholder="e.g., Acme Corporation"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? "Creating..." : "Create Organisation"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
